import { useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getModelOptions } from "../OrderForm.options";
import { OrderFormData } from "../OrderForm.schema";
import { Portfolio } from "@/api/types";

type Props = {
  portfolios: Portfolio[];
};

export function ModelPortfolioSelector({ portfolios }: Props) {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  const portfolioId = useWatch({ name: "dpPortfolioId" });
  const portType = useWatch({ name: "dpPortType" });
  const modelType = useWatch({ name: "dpModelType" });

  const selected = portfolios.find((p) => p.id === Number(portfolioId));
  const isNew = portfolioId === "NEW";

  const existingModelValue =
    selected?.modelPortfolio?.linkedPortfolio?.shortName ?? "";

  // Base options from portType, then merge in the portfolio's own model if not already present
  const modelOptions = useMemo(() => {
    const baseOptions = getModelOptions(portType ?? "");
    const existingModelLabel =
      existingModelValue || selected?.modelPortfolio?.code;
    return existingModelValue &&
      !baseOptions.find((o) => o.value === existingModelValue)
      ? [
          { value: existingModelValue, label: existingModelLabel },
          ...baseOptions,
        ]
      : baseOptions;
  }, [portType, existingModelValue, selected?.modelPortfolio?.code]);

  const modelOptionsRef = useRef(modelOptions);
  modelOptionsRef.current = modelOptions;

  const prevPortfolioIdRef = useRef(portfolioId);

  // Reset model type when portfolio selection changes
  useEffect(() => {
    if (portfolioId === prevPortfolioIdRef.current) return; // unchanged — skip (handles editing pre-fill)
    prevPortfolioIdRef.current = portfolioId;
    if (selected?.modelPortfolio?.linkedPortfolio?.shortName) {
      // Always use the portfolio's own linked model — never carry over a previous selection
      setValue(
        "dpModelType",
        selected.modelPortfolio.linkedPortfolio.shortName,
        { shouldValidate: true }
      );
      setValue(
        "dpModelTypeName",
        selected.modelPortfolio.linkedPortfolio.name ||
          selected.modelPortfolio.name
      );
    } else {
      // New portfolio or existing portfolio without a linked model — always reset
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
    }
  }, [portfolioId, selected, setValue]);

  // If current modelType is not in new options (after portType change), reset — but
  // never reset if the value comes from the selected portfolio's own model
  useEffect(() => {
    if (
      modelType &&
      modelType !== existingModelValue &&
      !modelOptions.find((o) => o.value === modelType)
    ) {
      setValue("dpModelType", "");
      setValue("dpModelTypeName", "");
    }
  }, [portType, modelOptions, modelType, existingModelValue, setValue]);

  return (
    <div className="grid grid-cols-[180px_1fr] items-center gap-3">
      <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
        Modeltype:
      </label>
      <select
        {...register("dpModelType")}
        disabled={!portType}
        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">-- Select model --</option>
        {modelOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {errors.dpModelType && (
        <p className="col-start-2 text-xs text-red-500">
          {errors.dpModelType.message}
        </p>
      )}
    </div>
  );
}
